import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  Image,
  StyleSheet,
  Svg,
  Rect,
} from "@react-pdf/renderer";
import QRCode from "qrcode";
import dayjs from "dayjs";
import { HOTEL_PDF_FONT_FAMILY } from "./hotelPdfFont";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { formatDate, getDurationInDays } from "../../../helpers/dateHelper";
import {
  hasHotelConfirmationNumber,
  getHotelConfirmationNumberForDisplay,
  formatHotelGuestDisplayName,
  formatRoomComboTitle,
  getHotelGuestRoomNumber,
} from "../../AllTickets/Hotel/hotelhelper";
import { getRoomMealCategoryLabel } from "../../../pages/Hotels/hotelMealTypes";

dayjs.extend(customParseFormat);

const NAVY = "#16324F";
const RED = "#C81E1E";
const INK = "#1C2B39";
const MUTED = "#5E6A75";
const LINE = "#E4EAF1";
const SOFT = "#F5F8FB";
const WHITE = "#FFFFFF";

const styles = StyleSheet.create({
  page: {
    padding: 28,
    fontFamily: HOTEL_PDF_FONT_FAMILY,
    backgroundColor: WHITE,
    color: INK,
  },
  accent: {
    height: 6,
    backgroundColor: RED,
    marginBottom: 16,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  brand: {
    width: "38%",
    paddingRight: 8,
  },
  centerTitle: {
    width: "24%",
    alignItems: "center",
    paddingTop: 0,
  },
  logo: {
    width: 78,
    height: 42,
    objectFit: "contain",
    marginBottom: 8,
  },
  agencyName: {
    fontSize: 12,
    fontWeight: 700,
    color: NAVY,
    marginBottom: 3,
  },
  address: {
    fontSize: 8,
    color: MUTED,
    lineHeight: 1.4,
  },
  docBlock: {
    width: "38%",
    alignItems: "flex-end",
  },
  docKicker: {
    fontSize: 9,
    color: RED,
    fontWeight: 700,
    marginBottom: 4,
    textAlign: "right",
  },
  docTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: NAVY,
    textAlign: "center",
  },
  docNo: {
    fontSize: 9,
    color: INK,
    marginBottom: 2,
  },
  docDate: {
    fontSize: 8,
    color: MUTED,
  },
  cardRow: {
    flexDirection: "row",
    marginBottom: 12,
  },
  card: {
    width: "49%",
    backgroundColor: SOFT,
    borderRadius: 6,
    padding: 10,
  },
  cardGap: {
    width: "2%",
  },
  cardLabel: {
    fontSize: 7,
    letterSpacing: 0.8,
    color: RED,
    fontWeight: 700,
    marginBottom: 4,
  },
  cardTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: NAVY,
    marginBottom: 3,
  },
  cardText: {
    fontSize: 8,
    color: MUTED,
    marginBottom: 2,
  },
  metaLine: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  metaKey: {
    fontSize: 8,
    color: MUTED,
    width: "46%",
  },
  metaVal: {
    fontSize: 8,
    fontWeight: 700,
    color: INK,
    width: "54%",
    textAlign: "right",
  },
  stay: {
    borderWidth: 1,
    borderColor: LINE,
    borderRadius: 6,
    padding: 10,
    marginBottom: 12,
  },
  stayTop: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  stayHotel: {
    width: "68%",
    paddingRight: 8,
  },
  stayName: {
    fontSize: 12,
    fontWeight: 700,
    color: NAVY,
    marginBottom: 2,
  },
  stayCity: {
    fontSize: 8,
    color: MUTED,
  },
  confirmBox: {
    width: "32%",
    backgroundColor: "#FDECEC",
    borderRadius: 4,
    padding: 6,
  },
  confirmLabel: {
    fontSize: 7,
    color: RED,
    fontWeight: 700,
    marginBottom: 2,
  },
  confirmValue: {
    fontSize: 9,
    fontWeight: 700,
    color: NAVY,
  },
  stayFacts: {
    flexDirection: "row",
    borderTopWidth: 1,
    borderTopColor: LINE,
    paddingTop: 8,
  },
  fact: {
    width: "16.66%",
  },
  factLabel: {
    fontSize: 7,
    color: MUTED,
    marginBottom: 2,
  },
  factValue: {
    fontSize: 9,
    fontWeight: 700,
    color: INK,
  },
  table: {
    borderWidth: 1,
    borderColor: LINE,
    borderRadius: 4,
    overflow: "hidden",
  },
  tableHead: {
    flexDirection: "row",
    backgroundColor: NAVY,
  },
  tableRow: {
    flexDirection: "row",
    borderTopWidth: 1,
    borderTopColor: LINE,
  },
  tableRowAlt: {
    backgroundColor: SOFT,
  },
  th: {
    fontSize: 7,
    fontWeight: 700,
    color: WHITE,
    padding: 5,
  },
  td: {
    fontSize: 7.5,
    color: INK,
    padding: 5,
  },
  totalsWrap: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginTop: 12,
  },
  qrBox: {
    width: "46%",
    alignItems: "flex-end",
  },
  totals: {
    width: "46%",
    borderWidth: 1,
    borderColor: LINE,
    borderRadius: 6,
    overflow: "hidden",
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    padding: "7 10",
    borderBottomWidth: 1,
    borderBottomColor: LINE,
  },
  totalLabel: {
    fontSize: 8,
    color: MUTED,
  },
  totalValue: {
    fontSize: 8,
    color: INK,
  },
  grandRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    padding: "8 10",
    backgroundColor: NAVY,
  },
  grandLabel: {
    fontSize: 9,
    fontWeight: 700,
    color: WHITE,
  },
  grandValue: {
    fontSize: 9,
    fontWeight: 700,
    color: WHITE,
  },
  footer: {
    marginTop: 16,
    borderWidth: 1,
    borderColor: LINE,
    borderRadius: 6,
    padding: 10,
  },
  footerHead: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  footerTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: "#111111",
  },
  footerNote: {
    fontSize: 7,
    color: MUTED,
  },
  bankTable: { borderWidth: 1, borderColor: "#111111" },
  bankHead: { flexDirection: "row" },
  bankBody: {
    flexDirection: "row",
    borderTopWidth: 1,
    borderTopColor: "#111111",
  },
  bankTh: {
    padding: 4,
    borderRightWidth: 1,
    borderRightColor: "#111111",
  },
  bankThText: {
    fontSize: 8,
    fontWeight: 700,
    color: "#111111",
  },
  bankTd: {
    padding: 4,
    borderRightWidth: 1,
    borderRightColor: "#111111",
    overflow: "hidden",
  },
  bankTdText: {
    fontSize: 8,
    color: "#111111",
  },
  bankCellLast: { borderRightWidth: 0 },
  termsTitle: {
    fontSize: 9,
    fontWeight: 700,
    color: RED,
    marginTop: 10,
    marginBottom: 4,
  },
  termLine: {
    fontSize: 7,
    color: INK,
    marginBottom: 2,
    lineHeight: 1.3,
  },
  note: {
    marginTop: 12,
    fontSize: 8,
    color: MUTED,
    textAlign: "center",
  },
});

const breakLongText = (value) =>
  String(value ?? "").replace(/[^\s]{12,}/g, (token) =>
    token.match(/.{1,12}/g).join("\u200b"),
  );

const joinParts = (parts) =>
  parts
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .join(", ");

const formatInvoiceDate = (value) => {
  const formatted = formatDate(value, "DD MMM YYYY");
  if (formatted) return formatted;
  if (value == null || value === "") return "—";
  const raw = String(value).trim();
  const parsed = dayjs(raw.includes("T") ? raw : raw.replace(" ", "T"));
  return parsed.isValid() ? parsed.format("DD MMM YYYY") : "—";
};

const money = (amount) => `₹ ${Number(amount || 0).toFixed(2)}`;

const InvoiceQr = ({ value }) => {
  let qr;
  try {
    qr = QRCode.create(String(value || "Invoice"), { errorCorrectionLevel: "M" });
  } catch {
    return null;
  }
  const count = qr.modules.size;
  const quiet = 2;
  const total = count + quiet * 2;
  const cells = [];
  for (let y = 0; y < count; y += 1) {
    for (let x = 0; x < count; x += 1) {
      if (qr.modules.get(x, y)) {
        cells.push(
          <Rect
            key={`${x}-${y}`}
            x={x + quiet}
            y={y + quiet}
            width={1}
            height={1}
            fill={NAVY}
          />,
        );
      }
    }
  }
  return (
    <Svg width={120} height={120} viewBox={`0 0 ${total} ${total}`}>
      <Rect x={0} y={0} width={total} height={total} fill={WHITE} />
      {cells}
    </Svg>
  );
};

const formatHotelAddress = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed || trimmed === "[object Object]") return "";
    if (trimmed.startsWith("{")) {
      try {
        return formatHotelAddress(JSON.parse(trimmed));
      } catch {
        return trimmed;
      }
    }
    return trimmed;
  }
  if (typeof value === "object") {
    return joinParts([
      value.address ?? value.addressLine ?? value.Address,
      value.cityName ?? value.city ?? value.City,
      value.state ?? value.State,
      value.postalCode ?? value.PostalCode ?? value.zip,
      value.countryName ?? value.CountryName,
    ]);
  }
  return "";
};

const HotelInvoiceDoc = ({ invoiceData, logo, type }) => {
  const isCreditNote = type === "credit_note";
  const guests = Array.isArray(invoiceData?.guests) ? invoiceData.guests : [];
  const leadGuest = formatHotelGuestDisplayName(guests[0] || {}) || "—";
  const showConfirmation = hasHotelConfirmationNumber(invoiceData);
  const confirmationNo = getHotelConfirmationNumberForDisplay(invoiceData);
  const bookingDate = formatInvoiceDate(invoiceData?.CreatedOn);
  const checkIn = formatInvoiceDate(invoiceData?.CheckInDate);
  const checkOut = formatInvoiceDate(invoiceData?.CheckOutDate);
  const nights = getDurationInDays(
    invoiceData?.CheckOutDate,
    invoiceData?.CheckInDate,
  );
  const documentNo = isCreditNote
    ? invoiceData?.creditNoteNo
    : invoiceData?.invoiceNo;
  const agencyName = String(
    invoiceData?.agentDetails?.AgencyName ??
      invoiceData?.agentDetails?.CompanyName ??
      "",
  ).trim();
  const address = invoiceData?.agentDetails
    ? joinParts([
        invoiceData.agentDetails.Address1,
        invoiceData.agentDetails.Address2,
        invoiceData.agentDetails.City,
        invoiceData.agentDetails.State,
      ])
    : String(invoiceData?.adminDetails?.[0]?.Address ?? "").trim();

  const rooms = Array.isArray(invoiceData?.Rooms) ? invoiceData.Rooms : [];
  const roomLabel = (room) =>
    formatRoomComboTitle(room?.roomName ?? room?.RoomName ?? "");
  const sharedRoomName = (() => {
    const names = [...new Set(rooms.map(roomLabel).filter(Boolean))];
    return names.length === 1 ? names[0] : "";
  })();
  const mealLabel = (room) => getRoomMealCategoryLabel(room) || "";
  const sharedMeal = (() => {
    const meals = [...new Set(rooms.map(mealLabel).filter(Boolean))];
    return meals.length === 1 ? meals[0] : "";
  })();
  const roomNameForGuest = (guest) => {
    const serial = String(
      guest?.RoomSerialNo ??
        guest?.roomSerialNo ??
        guest?.GuestInRoom ??
        guest?.guestInRoom ??
        "",
    ).trim();
    const matched = rooms.find(
      (room) => String(room?.roomId ?? room?.RoomId ?? "") === serial,
    );
    const byIndex = rooms[getHotelGuestRoomNumber(guest, 1) - 1];
    const name = roomLabel(matched) || roomLabel(byIndex) || sharedRoomName || "—";
    const meal = mealLabel(matched) || mealLabel(byIndex) || sharedMeal;
    return meal && name !== "—" ? `${name}, ${meal}` : name;
  };
  const starRating = Number(invoiceData?.StarRating ?? 0);

  const amountOf = (value) => Number(value) || 0;
  const roomRate = rooms.reduce(
    (sum, room) => sum + amountOf(room?.base ?? room?.Base),
    0,
  );
  const roomTax = rooms.reduce(
    (sum, room) => sum + amountOf(room?.tax ?? room?.Tax),
    0,
  );
  const otherCharges = rooms.reduce(
    (sum, room) => sum + amountOf(room?.otherCharges ?? room?.OtherCharges),
    0,
  );
  const serviceCharge = amountOf(invoiceData?.Conveniencefee);
  const currency = String(
    rooms.find((room) => room?.currency)?.currency || "INR",
  ).trim();
  const guestNames = guests
    .map((guest) => formatHotelGuestDisplayName(guest))
    .filter(Boolean)
    .join(", ");
  const qrValue = [
    `Invoice No: ${documentNo || "-"}`,
    `Booking Date: ${bookingDate}`,
    `Reference No: ${invoiceData?.RefNumber || "-"}`,
    `Hotel: ${invoiceData?.HotelName || "-"}`,
    `City: ${invoiceData?.CityName || "-"}`,
    `Check-in: ${checkIn}`,
    `Check-out: ${checkOut}`,
    `Nights: ${nights > 0 ? nights : "-"}`,
    `Rooms: ${invoiceData?.NoOfRooms ?? "-"}`,
    `No. of Guests: ${guests.length || "-"}`,
    `Guests: ${guestNames || "-"}`,
    `Star Rating: ${starRating}`,
    `Meal Preference: ${
      [...new Set(rooms.map(mealLabel).filter(Boolean))].join(", ") || "-"
    }`,
    `PAN: ${String(invoiceData?.agentDetails?.PAN ?? "").trim() || "-"}`,
    showConfirmation ? `Confirmation No: ${confirmationNo}` : "",
    `Grand Total: ${money(invoiceData?.subTotal)}`,
    `Currency: ${currency}`,
  ]
    .filter(Boolean)
    .join("\n");
  const bankColumns = [
    { key: "bankName", label: "Bank Name", width: "18%" },
    { key: "accountName", label: "Account Name", width: "16%" },
    { key: "accountNo", label: "Acc No / IBAN No", width: "22%" },
    { key: "ifsc", label: "IFSC Code", width: "14%" },
    { key: "branch", label: "Branch", width: "16%" },
    { key: "country", label: "Country", width: "14%" },
  ];
  const banks = [
    {
      bankName: "HDFC Bank Ltd ( INDIA )",
      accountName: "SHOP YOUR TRIP PVT LTD",
      accountNo: "50200056477211",
      ifsc: "HDFC0003717",
      branch: "HDFC Bank Ltd",
      country: "India",
    },
    {
      bankName: "ICICI BANK LTD ( INDIA )",
      accountName: "ICICI BANK LTD",
      accountNo: "418005000835",
      ifsc: "ICIC0004180",
      branch: "ICICI BANK LTD",
      country: "India",
    },
    {
      bankName: "IDFC FIRST BANK ( INDIA )",
      accountName: "IDFC FIRST BANK",
      accountNo: "10093620851",
      ifsc: "IDFB0020137",
      branch: "Nehru Place",
      country: "India",
    },
    {
      bankName: "WESTPAC BANKING CORPORATION ( AUSTRALIA )",
      accountName: "SHOP YOUR TRIP PTY LTD",
      accountNo: "723441",
      ifsc: "032-778",
      branch: "CANBERRA",
      country: "Australia",
    },
    {
      bankName: "RAK BANK ( UAE )",
      accountName: "SYT TRAVEL & TOURISM LLC",
      accountNo: "0033146717001",
      ifsc: "NRAKAEAK",
      branch: "Deira - Riggat Al Buteen",
      country: "United Arab Emirates",
    },
    {
      bankName: "Paysera LT, UAB ( EUROPE )",
      accountName: "SASTA EUROPE",
      accountNo: "LT913500010017371289",
      ifsc: "EVIULT2VXXX",
      branch: "VILNIUS, Lithuania",
      country: "Lithuania",
    },
    {
      bankName: "BANK OF AMERICA ( USA )",
      accountName: "SASTA USA TOURS LLC",
      accountNo: "325200966946",
      ifsc: "BOFAUS3N",
      branch: "855 POLLASKY AVE, CLOVIS CA 93612",
      country: "United States",
    },
    {
      bankName: "REVOLUT BANK ( EUROPE )",
      accountName: "SASTA EUROPE UAB",
      accountNo: "LT883250012552750928",
      ifsc: "CHASDEFX",
      branch: "Revolut Bank UAB",
      country: "Lithuania",
    },
  ];
  const hotelAddress = formatHotelAddress(invoiceData?.HotelAddress);
  const contactPhone = String(
    invoiceData?.agentDetails?.Phone ??
      invoiceData?.phoneNo ??
      invoiceData?.adminDetails?.[0]?.PhoneNumber ??
      "",
  ).trim();
  const contactEmail = String(
    invoiceData?.agentDetails?.Email ??
      invoiceData?.adminDetails?.[0]?.Email ??
      "",
  ).trim();
  const panNumber = String(invoiceData?.agentDetails?.PAN ?? "").trim();
  const columns = [
    { key: "guest", label: "Guest", width: "34%" },
    { key: "rooms", label: "Room", width: "16%" },
    { key: "room", label: "Room Name", width: "50%" },
  ];

  const cellValue = (key, guest) => {
    if (key === "guest") return formatHotelGuestDisplayName(guest) || "—";
    if (key === "rooms") return `Room ${getHotelGuestRoomNumber(guest, 1)}`;
    if (key === "room") return roomNameForGuest(guest);
    return "";
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.accent} />

        <View style={styles.header}>
          <View style={styles.brand}>
            {logo ? (
              <Image
                style={styles.logo}
                src={
                  typeof Blob !== "undefined" && logo instanceof Blob
                    ? logo
                    : String(logo).startsWith("data:") ||
                        String(logo).startsWith("http")
                      ? logo
                      : process.env.REACT_APP_BASE_URL +
                        String(logo).replace(/^\.\//, "").replace(/^\//, "")
                }
              />
            ) : null}
            {agencyName ? (
              <Text style={styles.agencyName}>{agencyName}</Text>
            ) : null}
            {address ? <Text style={styles.address}>{address}</Text> : null}
            {contactPhone ? (
              <Text style={styles.address}>Phone: {contactPhone}</Text>
            ) : null}
            {contactEmail ? (
              <Text style={styles.address}>Email: {contactEmail}</Text>
            ) : null}
            {panNumber ? (
              <Text style={styles.address}>PAN: {panNumber}</Text>
            ) : null}
          </View>

          <View style={styles.centerTitle}>
            <Text style={styles.docTitle}>
              {isCreditNote ? "Credit Note" : "Invoice"}
            </Text>
          </View>

          <View style={styles.docBlock}>
            <Text style={styles.docKicker}>Hotel Booking Details</Text>
            <Text style={styles.docDate}>
              {isCreditNote ? "Credit Note No." : "Invoice No."}
            </Text>
            <Text style={styles.docNo}>{documentNo || "—"}</Text>
            <Text style={styles.docDate}>
              {isCreditNote ? "Credit Note Date" : "Invoice Date"}
            </Text>
            <Text style={styles.docNo}>{bookingDate}</Text>
          </View>
        </View>

        <View style={styles.cardRow}>
          <View style={styles.card}>
            <Text style={styles.cardLabel}>BILLED TO</Text>
            <Text style={styles.cardTitle}>{leadGuest}</Text>
            {invoiceData?.email ? (
              <Text style={styles.cardText}>{invoiceData.email}</Text>
            ) : null}
            {invoiceData?.phoneNo ? (
              <Text style={styles.cardText}>{invoiceData.phoneNo}</Text>
            ) : null}
            {invoiceData?.cinNo ? (
              <Text style={styles.cardText}>CIN {invoiceData.cinNo}</Text>
            ) : null}
          </View>
          <View style={styles.cardGap} />
          <View style={styles.card}>
            <Text style={styles.cardLabel}>BOOKING</Text>
            <View style={styles.metaLine}>
              <Text style={styles.metaKey}>Reference No.</Text>
              <Text style={styles.metaVal}>{invoiceData?.RefNumber || "—"}</Text>
            </View>
            <View style={styles.metaLine}>
              <Text style={styles.metaKey}>Booking Date</Text>
              <Text style={styles.metaVal}>{bookingDate}</Text>
            </View>
            {showConfirmation ? (
              <View style={styles.metaLine}>
                <Text style={styles.metaKey}>Confirmation No.</Text>
                <Text style={styles.metaVal}>{confirmationNo}</Text>
              </View>
            ) : null}
          </View>
        </View>

        <View style={styles.stay}>
          <View style={styles.stayTop}>
            <View style={{ width: "100%" }}>
              <Text style={styles.stayName}>
                {invoiceData?.HotelName || "Hotel"}
              </Text>
              {invoiceData?.CityName ? (
                <Text style={styles.stayCity}>{invoiceData.CityName}</Text>
              ) : null}
              {hotelAddress ? (
                <Text style={styles.stayCity}>{hotelAddress}</Text>
              ) : null}
            </View>
          </View>
          <View style={styles.stayFacts}>
            <View style={styles.fact}>
              <Text style={styles.factLabel}>Check-in</Text>
              <Text style={styles.factValue}>{checkIn}</Text>
            </View>
            <View style={styles.fact}>
              <Text style={styles.factLabel}>Check-out</Text>
              <Text style={styles.factValue}>{checkOut}</Text>
            </View>
            <View style={styles.fact}>
              <Text style={styles.factLabel}>Nights</Text>
              <Text style={styles.factValue}>{nights > 0 ? nights : "—"}</Text>
            </View>
            <View style={styles.fact}>
              <Text style={styles.factLabel}>Rooms</Text>
              <Text style={styles.factValue}>
                {invoiceData?.NoOfRooms ?? "—"}
              </Text>
            </View>
            <View style={styles.fact}>
              <Text style={styles.factLabel}>Guests</Text>
              <Text style={styles.factValue}>{guests.length || "—"}</Text>
            </View>
            <View style={styles.fact}>
              <Text style={styles.factLabel}>Star Rating</Text>
              <Text style={styles.factValue}>{starRating}</Text>
            </View>
          </View>
        </View>

        <View style={styles.table}>
          <View style={styles.tableHead}>
            {columns.map((column) => (
              <Text key={column.key} style={{ ...styles.th, width: column.width }}>
                {column.label}
              </Text>
            ))}
          </View>
          {guests.map((guest, index) => (
            <View
              key={`${guest?.FirstName || "guest"}-${index}`}
              style={
                index % 2 === 1
                  ? { ...styles.tableRow, ...styles.tableRowAlt }
                  : styles.tableRow
              }
            >
              {columns.map((column) => (
                <Text
                  key={column.key}
                  style={{ ...styles.td, width: column.width }}
                >
                  {cellValue(column.key, guest)}
                </Text>
              ))}
            </View>
          ))}
        </View>

        <View style={styles.totalsWrap}>
          <View style={styles.totals}>
            {roomRate > 0 ? (
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Room Rate</Text>
                <Text style={styles.totalValue}>{money(roomRate)}</Text>
              </View>
            ) : null}
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Tax</Text>
              <Text style={styles.totalValue}>{money(roomTax)}</Text>
            </View>
            {otherCharges > 0 ? (
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Other Charges</Text>
                <Text style={styles.totalValue}>{money(otherCharges)}</Text>
              </View>
            ) : null}
            {serviceCharge > 0 ? (
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Service Charges</Text>
                <Text style={styles.totalValue}>{money(serviceCharge)}</Text>
              </View>
            ) : null}
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Currency</Text>
              <Text style={styles.totalValue}>{currency}</Text>
            </View>
            {isCreditNote ? (
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Refunded Amount</Text>
                <Text style={styles.totalValue}>
                  {money(invoiceData?.refundAmount)}
                </Text>
              </View>
            ) : null}
            <View style={styles.grandRow}>
              <Text style={styles.grandLabel}>Grand Total</Text>
              <Text style={styles.grandValue}>
                {money(invoiceData?.subTotal)}
              </Text>
            </View>
          </View>
          <View style={styles.qrBox}>
            <InvoiceQr value={qrValue} />
          </View>
        </View>

        <View style={styles.footer}>
          <View style={styles.footerHead}>
            <Text style={styles.footerTitle}>Bank Details(Current Account)</Text>
            <Text style={styles.footerNote}>E. & O.E.</Text>
          </View>
          <View style={styles.bankTable}>
            <View style={styles.bankHead} wrap={false}>
              {bankColumns.map((column, index) => (
                <View
                  key={column.key}
                  style={{
                    ...styles.bankTh,
                    width: column.width,
                    ...(index === bankColumns.length - 1
                      ? styles.bankCellLast
                      : {}),
                  }}
                >
                  <Text style={styles.bankThText}>{column.label}</Text>
                </View>
              ))}
            </View>
            {banks.map((bank) => (
              <View key={bank.accountNo} style={styles.bankBody} wrap={false}>
                {bankColumns.map((column, index) => (
                  <View
                    key={column.key}
                    style={{
                      ...styles.bankTd,
                      width: column.width,
                      ...(index === bankColumns.length - 1
                        ? styles.bankCellLast
                        : {}),
                    }}
                  >
                    <Text style={styles.bankTdText}>
                      {breakLongText(bank[column.key])}
                    </Text>
                  </View>
                ))}
              </View>
            ))}
          </View>
          <Text style={styles.termsTitle}>Terms & Conditions</Text>
          <Text style={styles.termLine}>
            CASH: Payment to be made to the cashier and a printed official receipt must be obtained.
          </Text>
          <Text style={styles.termLine}>
            CHEQUE: All cheques or demand drafts must be crossed A/c Payee Only and drawn in favour of SHOP YOUR TRIP PRIVATE LIMITED.
          </Text>
          <Text style={styles.termLine}>
            LATE PAYMENT: Interest at 24% per annum will be charged on all outstanding bills after the due date.
          </Text>
          <Text style={styles.termLine}>
            VERY IMP: Kindly check all details carefully to avoid unnecessary complications.
          </Text>
        </View>

        <Text style={styles.note}>
          This is an electronically generated{" "}
          {isCreditNote ? "credit note" : "invoice"} and does not require a
          signature.
        </Text>
      </Page>
    </Document>
  );
};

export default HotelInvoiceDoc;
